import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Database, Settings, AlertCircle } from 'lucide-react';
import { GlobalMetrics } from '@/hooks/use-global-metrics';

interface GlobalMetricsCardsProps {
  metrics: GlobalMetrics;
}

export function GlobalMetricsCards({ metrics }: GlobalMetricsCardsProps) {
  const itTotalTasks = metrics.itTasksPending + metrics.itTasksInProgress + metrics.itTasksCompleted + metrics.itTasksBlocked;
  const dataTotalTasks = metrics.dataTasksPending + metrics.dataTasksInProgress + metrics.dataTasksCompleted + metrics.dataTasksBlocked;
  const platformTotalTasks = metrics.platformTasksPending + metrics.platformTasksInProgress + metrics.platformTasksCompleted;

  const itCompletionPercent = itTotalTasks > 0 ? Math.round((metrics.itTasksCompleted / itTotalTasks) * 100) : 0;
  const dataCompletionPercent = dataTotalTasks > 0 ? Math.round((metrics.dataTasksCompleted / dataTotalTasks) * 100) : 0;

  return (
    <div className='space-y-4'>
      {/* Row 1: Organization Overview */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
        <Card>
          <CardHeader className='flex flex-row items-center justify-between pb-2'>
            <CardTitle className='text-sm font-medium text-gray-600 dark:text-gray-400'>
              Total Organizations
            </CardTitle>
            <Building2 className='h-4 w-4 text-blue-600' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{metrics.totalOrganizations}</div>
            <p className='text-xs text-gray-600 dark:text-gray-400 mt-1'>
              All organizations in system
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between pb-2'>
            <CardTitle className='text-sm font-medium text-gray-600 dark:text-gray-400'>
              Total Locations
            </CardTitle>
            <AlertCircle className='h-4 w-4 text-orange-600' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{metrics.totalLocations}</div>
            <p className='text-xs text-gray-600 dark:text-gray-400 mt-1'>
              Across all organizations
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between pb-2'>
            <CardTitle className='text-sm font-medium text-gray-600 dark:text-gray-400'>
              In Pipeline
            </CardTitle>
            <Settings className='h-4 w-4 text-blue-600' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{metrics.organizationsInPipeline}</div>
            <p className='text-xs text-gray-600 dark:text-gray-400 mt-1'>
              Not yet activated or rejected
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Task Summary Cards */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        {/* IT Tasks Card */}
        <Card className='hover:shadow-lg transition-shadow border-l-4 border-l-orange-500'>
          <CardHeader className='pb-3'>
            <div className='flex items-center justify-between'>
              <CardTitle className='text-sm font-medium text-orange-600 dark:text-orange-400 flex items-center gap-2'>
                <Settings className='h-4 w-4' />
                IT Tasks - Global
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className='space-y-3'>
            <div className='space-y-2'>
              <div className='flex justify-between items-center text-sm'>
                <span className='text-gray-600 dark:text-gray-400'>Pending</span>
                <span className='font-semibold text-yellow-600 dark:text-yellow-400'>
                  {metrics.itTasksPending}
                </span>
              </div>
              <div className='flex justify-between items-center text-sm'>
                <span className='text-gray-600 dark:text-gray-400'>In Progress</span>
                <span className='font-semibold text-blue-600 dark:text-blue-400'>
                  {metrics.itTasksInProgress}
                </span>
              </div>
              <div className='flex justify-between items-center text-sm'>
                <span className='text-gray-600 dark:text-gray-400'>Completed</span>
                <span className='font-semibold text-green-600 dark:text-green-400'>
                  {metrics.itTasksCompleted}
                </span>
              </div>
              {metrics.itTasksBlocked > 0 && (
                <div className='flex justify-between items-center text-sm'>
                  <span className='text-gray-600 dark:text-gray-400'>Blocked</span>
                  <span className='font-semibold text-red-600 dark:text-red-400'>
                    {metrics.itTasksBlocked}
                  </span>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {itTotalTasks > 0 && (
              <div className='pt-2 border-t border-gray-200 dark:border-gray-700'>
                <div className='flex justify-between items-center mb-1'>
                  <span className='text-xs text-gray-600 dark:text-gray-400'>Progress</span>
                  <span className='text-xs font-semibold text-orange-600 dark:text-orange-400'>
                    {itCompletionPercent}%
                  </span>
                </div>
                <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2'>
                  <div
                    className='bg-orange-500 h-2 rounded-full transition-all'
                    style={{ width: `${itCompletionPercent}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Data Tasks Card */}
        <Card className='hover:shadow-lg transition-shadow border-l-4 border-l-green-500'>
          <CardHeader className='pb-3'>
            <div className='flex items-center justify-between'>
              <CardTitle className='text-sm font-medium text-green-600 dark:text-green-400 flex items-center gap-2'>
                <Database className='h-4 w-4' />
                Data Tasks - Global
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className='space-y-3'>
            <div className='space-y-2'>
              <div className='flex justify-between items-center text-sm'>
                <span className='text-gray-600 dark:text-gray-400'>Pending</span>
                <span className='font-semibold text-yellow-600 dark:text-yellow-400'>
                  {metrics.dataTasksPending}
                </span>
              </div>
              <div className='flex justify-between items-center text-sm'>
                <span className='text-gray-600 dark:text-gray-400'>In Progress</span>
                <span className='font-semibold text-blue-600 dark:text-blue-400'>
                  {metrics.dataTasksInProgress}
                </span>
              </div>
              <div className='flex justify-between items-center text-sm'>
                <span className='text-gray-600 dark:text-gray-400'>Completed</span>
                <span className='font-semibold text-green-600 dark:text-green-400'>
                  {metrics.dataTasksCompleted}
                </span>
              </div>
              {metrics.dataTasksBlocked > 0 && (
                <div className='flex justify-between items-center text-sm'>
                  <span className='text-gray-600 dark:text-gray-400'>Blocked</span>
                  <span className='font-semibold text-red-600 dark:text-red-400'>
                    {metrics.dataTasksBlocked}
                  </span>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {dataTotalTasks > 0 && (
              <div className='pt-2 border-t border-gray-200 dark:border-gray-700'>
                <div className='flex justify-between items-center mb-1'>
                  <span className='text-xs text-gray-600 dark:text-gray-400'>Progress</span>
                  <span className='text-xs font-semibold text-green-600 dark:text-green-400'>
                    {dataCompletionPercent}%
                  </span>
                </div>
                <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2'>
                  <div
                    className='bg-green-500 h-2 rounded-full transition-all'
                    style={{ width: `${dataCompletionPercent}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Platform Tasks Card */}
        <Card className='hover:shadow-lg transition-shadow border-l-4 border-l-blue-500'>
          <CardHeader className='pb-3'>
            <div className='flex items-center justify-between'>
              <CardTitle className='text-sm font-medium text-blue-600 dark:text-blue-400 flex items-center gap-2'>
                <AlertCircle className='h-4 w-4' />
                Platform Tasks - Global
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className='space-y-3'>
            <div className='space-y-2'>
              <div className='flex justify-between items-center text-sm'>
                <span className='text-gray-600 dark:text-gray-400'>Pending</span>
                <span className='font-semibold text-yellow-600 dark:text-yellow-400'>
                  {metrics.platformTasksPending}
                </span>
              </div>
              <div className='flex justify-between items-center text-sm'>
                <span className='text-gray-600 dark:text-gray-400'>In Progress</span>
                <span className='font-semibold text-blue-600 dark:text-blue-400'>
                  {metrics.platformTasksInProgress}
                </span>
              </div>
              <div className='flex justify-between items-center text-sm'>
                <span className='text-gray-600 dark:text-gray-400'>Completed</span>
                <span className='font-semibold text-green-600 dark:text-green-400'>
                  {metrics.platformTasksCompleted}
                </span>
              </div>
            </div>

            {/* Progress Bar */}
            {platformTotalTasks > 0 && (
              <div className='pt-2 border-t border-gray-200 dark:border-gray-700'>
                <div className='flex justify-between items-center mb-1'>
                  <span className='text-xs text-gray-600 dark:text-gray-400'>Progress</span>
                  <span className='text-xs font-semibold text-blue-600 dark:text-blue-400'>
                    {Math.round((metrics.platformTasksCompleted / platformTotalTasks) * 100)}%
                  </span>
                </div>
                <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2'>
                  <div
                    className='bg-blue-500 h-2 rounded-full transition-all'
                    style={{ width: `${Math.round((metrics.platformTasksCompleted / platformTotalTasks) * 100)}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
